import { supabase } from '../supabase';
import { createWarehouseError } from '../errors/warehouseErrors';

export enum AuditEventType {
  // Zone operations
  ZONE_CREATED = 'ZONE_CREATED',
  ZONE_UPDATED = 'ZONE_UPDATED',
  ZONE_DELETED = 'ZONE_DELETED',
  ZONE_ACTIVATED = 'ZONE_ACTIVATED',
  ZONE_DEACTIVATED = 'ZONE_DEACTIVATED',

  // Stock operations
  STOCK_IN = 'STOCK_IN',
  STOCK_OUT = 'STOCK_OUT',
  STOCK_ADJUSTMENT = 'STOCK_ADJUSTMENT',
  STOCK_MOVEMENT = 'STOCK_MOVEMENT',

  // Zone storage operations
  PRODUCT_STORED = 'PRODUCT_STORED',
  PRODUCT_REMOVED = 'PRODUCT_REMOVED',
  STORAGE_UPDATED = 'STORAGE_UPDATED',
  ZONE_DESTOCKED = 'ZONE_DESTOCKED',
  ZONE_TRANSFER = 'ZONE_TRANSFER',

  // System operations
  ZONE_USAGE_UPDATED = 'ZONE_USAGE_UPDATED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  VALIDATION_FAILED = 'VALIDATION_FAILED'
}

interface AuditChange {
  from: unknown;
  to: unknown;
}

interface AuditMetadata {
  timestamp?: string;
  context?: string;
  operation?: string;
  zone_id?: string;
  zone_name?: string;
  product_id?: string;
  quantity?: number;
  notes?: string;
  affected_zones?: string[];
  total_quantity?: number;
  zone_count?: number;
  zone_code?: string;
  zone_location?: string;
  fields_changed?: string[];
  quantity_change?: number;
  usage_change?: number;
  quantity_removed?: number;
  error_message?: string;
  error_code?: string;
  error_details?: unknown;
  stack_trace?: string;
  validation_error?: string;
  attempted_values?: Record<string, unknown>;
  source_zone_id?: string;
  source_zone_name?: string;
}

export interface AuditLogEntry {
  id?: string;
  organization_id: string;
  user_id: string;
  event_type: AuditEventType;
  entity_type: string;
  entity_id: string;
  entity_name?: string;
  changes?: Record<string, AuditChange>;
  metadata?: AuditMetadata;
  ip_address?: string;
  user_agent?: string;
  created_at?: string;
}

export interface AuditContext {
  organizationId: string;
  userId: string;
  ipAddress?: string;
  userAgent?: string;
}

export class AuditTrailService {
  /**
   * Log a warehouse operation to the audit trail
   */
  static async logOperation(
    context: AuditContext,
    eventType: AuditEventType,
    entityType: string,
    entityId: string,
    options?: {
      entityName?: string;
      changes?: Record<string, AuditChange>;
      metadata?: Partial<AuditMetadata>;
    }
  ): Promise<void> {
    try {
      // Map to the actual audit_logs table structure
      const auditEntry = {
        organization_id: context.organizationId,
        user_id: context.userId,
        entity_type: this.mapEntityType(entityType),
        entity_id: entityId,
        entity_name: options?.entityName,
        action_type: this.mapEventTypeToAction(eventType),
        action_category: 'warehouse_operations',
        change_note: options?.metadata?.operation || eventType,
        metadata: {
          event_type: eventType,
          ...options?.metadata,
          changes: options?.changes,
          timestamp: new Date().toISOString(),
          context: 'warehouse_operations'
        },
        source_system: 'web_app',
        ip_address: context.ipAddress,
        user_agent: context.userAgent
      };

      const { error } = await supabase
        .from('audit_logs')
        .insert([auditEntry]);

      if (error) throw error;

    } catch (error) {
      console.error('Failed to log audit entry:', error);
      throw createWarehouseError(error, 'audit logging');
    }
  }

  /**
   * Log zone creation
   */
  static async logZoneCreated(
    context: AuditContext,
    zoneId: string,
    zoneName: string,
    zoneData: Record<string, unknown>
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.ZONE_CREATED, 'warehouse_zone', zoneId, {
      entityName: zoneName,
      changes: { created: zoneData },
      metadata: {
        operation: 'create_zone',
        zone_code: zoneData.code,
        zone_location: zoneData.location
      }
    });
  }

  /**
   * Log zone update
   */
  static async logZoneUpdated(
    context: AuditContext,
    zoneId: string,
    zoneName: string,
    oldData: Record<string, unknown>,
    newData: Record<string, unknown>
  ): Promise<void> {
    const changes = this.calculateChanges(oldData, newData);
    
    await this.logOperation(context, AuditEventType.ZONE_UPDATED, 'warehouse_zone', zoneId, {
      entityName: zoneName,
      changes,
      metadata: {
        operation: 'update_zone',
        fields_changed: Object.keys(changes)
      }
    });
  }

  /**
   * Log zone deletion/deactivation
   */
  static async logZoneDeleted(
    context: AuditContext,
    zoneId: string,
    zoneName: string
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.ZONE_DELETED, 'warehouse_zone', zoneId, {
      entityName: zoneName,
      changes: { is_active: { from: true, to: false } },
      metadata: {
        operation: 'soft_delete_zone'
      }
    });
  }

  /**
   * Log product stored in zone
   */
  static async logProductStored(
    context: AuditContext,
    productId: string,
    productName: string,
    zoneId: string,
    zoneName: string,
    quantity: number,
    notes?: string
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.PRODUCT_STORED, 'product_zone_storage', productId, {
      entityName: productName,
      changes: { quantity: { from: 0, to: quantity } },
      metadata: {
        operation: 'store_product',
        zone_id: zoneId,
        zone_name: zoneName,
        quantity,
        notes
      }
    });
  }

  /**
   * Log product removed from zone
   */
  static async logProductRemoved(
    context: AuditContext,
    storageId: string,
    productId: string,
    productName: string,
    zoneId: string,
    zoneName: string,
    quantity: number
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.PRODUCT_REMOVED, 'product_zone_storage', storageId, {
      entityName: productName,
      changes: { quantity: { from: quantity, to: 0 } },
      metadata: {
        operation: 'remove_product',
        product_id: productId,
        zone_id: zoneId,
        zone_name: zoneName,
        quantity_removed: quantity
      }
    });
  }

  /**
   * Log zone destocking operation
   */
  static async logZoneDestocked(
    context: AuditContext,
    productId: string,
    productName: string,
    totalQuantity: number,
    affectedZones: string[]
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.ZONE_DESTOCKED, 'product', productId, {
      entityName: productName,
      changes: { total_destocked: totalQuantity },
      metadata: {
        operation: 'destock_from_zones',
        affected_zones: affectedZones,
        total_quantity: totalQuantity,
        zone_count: affectedZones.length
      }
    });
  }

  /**
   * Log storage quantity update
   */
  static async logStorageUpdated(
    context: AuditContext,
    storageId: string,
    productName: string,
    zoneId: string,
    zoneName: string,
    oldQuantity: number,
    newQuantity: number,
    notes?: string
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.STORAGE_UPDATED, 'product_zone_storage', storageId, {
      entityName: productName,
      changes: { quantity: { from: oldQuantity, to: newQuantity } },
      metadata: {
        operation: 'update_storage',
        zone_id: zoneId,
        zone_name: zoneName,
        quantity_change: newQuantity - oldQuantity,
        notes
      }
    });
  }

  /**
   * Log zone transfer operation
   */
  static async logZoneTransfer(
    context: AuditContext,
    productId: string,
    productName: string,
    sourceZoneId: string,
    sourceZoneName: string,
    destZoneId: string,
    destZoneName: string,
    quantity: number,
    notes?: string
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.ZONE_TRANSFER, 'product_zone_storage', productId, {
      entityName: productName,
      changes: {
        source_zone: { from: sourceZoneName, to: null },
        destination_zone: { from: null, to: destZoneName },
        quantity: { from: quantity, to: quantity }
      },
      metadata: {
        operation: 'zone_transfer',
        zone_id: destZoneId,
        zone_name: destZoneName,
        quantity,
        notes,
        source_zone_id: sourceZoneId,
        source_zone_name: sourceZoneName
      }
    });
  }

  /**
   * Log zone usage update
   */
  static async logZoneUsageUpdated(
    context: AuditContext,
    zoneId: string,
    zoneName: string,
    oldUsage: number,
    newUsage: number
  ): Promise<void> {
    await this.logOperation(context, AuditEventType.ZONE_USAGE_UPDATED, 'warehouse_zone', zoneId, {
      entityName: zoneName,
      changes: { current_usage: { from: oldUsage, to: newUsage } },
      metadata: {
        operation: 'update_zone_usage',
        usage_change: newUsage - oldUsage
      }
    });
  }

  /**
   * Log transaction failure for debugging
   */
  static async logTransactionFailed(
    context: AuditContext,
    operation: string,
    entityId: string,
    error: unknown
  ): Promise<void> {
    try {
      await this.logOperation(context, AuditEventType.TRANSACTION_FAILED, 'transaction', entityId, {
        entityName: operation,
        metadata: {
          operation,
          error_message: error.message,
          error_code: error.code,
          error_details: error.details,
          stack_trace: process.env.NODE_ENV === 'development' ? error.stack : undefined
        }
      });
    } catch (auditError) {
      console.error('Failed to log transaction failure:', auditError);
    }
  }

  /**
   * Log validation failure
   */
  static async logValidationFailed(
    context: AuditContext,
    operation: string,
    entityId: string,
    validationError: string,
    attemptedValues: Record<string, unknown>
  ): Promise<void> {
    try {
      await this.logOperation(context, AuditEventType.VALIDATION_FAILED, 'validation', entityId, {
        entityName: operation,
        metadata: {
          operation,
          validation_error: validationError,
          attempted_values: attemptedValues
        }
      });
    } catch (auditError) {
      console.error('Failed to log validation failure:', auditError);
    }
  }

  /**
   * Retrieve audit logs for a specific entity
   */
  static async getAuditLogs(
    organizationId: string,
    filters?: {
      entityType?: string;
      entityId?: string;
      eventType?: AuditEventType;
      userId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
    }
  ): Promise<AuditLogEntry[]> {
    try {
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user:user_profiles(id, first_name, last_name, email)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }
      if (filters?.entityId) {
        query = query.eq('entity_id', filters.entityId);
      }
      if (filters?.eventType) {
        // Search in metadata for event_type since it's stored there now
        query = query.contains('metadata', { event_type: filters.eventType });
      }
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data: logs, error } = await query;

      if (error) throw error;

      return logs || [];

    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw createWarehouseError(error, 'fetch audit logs');
    }
  }

  /**
   * Map entity types to compatible types for existing audit_logs table
   */
  private static mapEntityType(entityType: string): string {
    // Map warehouse entity types to existing supported types
    switch (entityType) {
      case 'product':
      case 'product_zone_storage':
      case 'warehouse_zone':
      case 'stock_movement':
      case 'zone_transfer':
      case 'stock_reservation':
      case 'inventory_adjustment':
        return 'note'; // Use 'note' as a generic type for warehouse operations
      case 'rental':
        return 'deal'; // Rentals are similar to deals
      case 'transaction':
      case 'validation':
        return 'task'; // System operations as tasks
      default:
        return entityType; // Keep original if already supported
    }
  }

  /**
   * Map event types to action types compatible with audit_logs table
   */
  private static mapEventTypeToAction(eventType: AuditEventType): string {
    // Map to existing supported action types only
    switch (eventType) {
      case AuditEventType.ZONE_CREATED:
        return 'create';
      case AuditEventType.ZONE_UPDATED:
      case AuditEventType.STORAGE_UPDATED:
      case AuditEventType.ZONE_USAGE_UPDATED:
      case AuditEventType.STOCK_ADJUSTMENT:
        return 'update';
      case AuditEventType.ZONE_DELETED:
        return 'delete';
      case AuditEventType.ZONE_ACTIVATED:
      case AuditEventType.ZONE_DEACTIVATED:
        return 'status_change';
      case AuditEventType.STOCK_IN:
      case AuditEventType.PRODUCT_STORED:
        return 'create'; // Use create for stock additions
      case AuditEventType.STOCK_OUT:
      case AuditEventType.PRODUCT_REMOVED:
      case AuditEventType.ZONE_DESTOCKED:
        return 'delete'; // Use delete for stock removals
      case AuditEventType.STOCK_MOVEMENT:
        return 'update'; // Use update for movements
      case AuditEventType.TRANSACTION_FAILED:
      case AuditEventType.VALIDATION_FAILED:
        return 'bulk_update'; // Use bulk_update for system operations
      default:
        return 'update';
    }
  }

  /**
   * Calculate changes between old and new data
   */
  private static calculateChanges(
    oldData: Record<string, unknown>,
    newData: Record<string, unknown>
  ): Record<string, AuditChange> {
    const changes: Record<string, AuditChange> = {};

    for (const [key, newValue] of Object.entries(newData)) {
      const oldValue = oldData[key];
      if (oldValue !== newValue) {
        changes[key] = { from: oldValue, to: newValue };
      }
    }

    return changes;
  }

  /**
   * Get summary statistics for audit logs
   */
  static async getAuditStatistics(
    organizationId: string,
    timeframe: 'day' | 'week' | 'month' = 'week'
  ): Promise<{
    totalEvents: number;
    eventsByType: Record<string, number>;
    topUsers: Array<{ userId: string; userName: string; eventCount: number }>;
    recentActivity: AuditLogEntry[];
  }> {
    const startDate = new Date();
    switch (timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
    }

    const logs = await this.getAuditLogs(organizationId, {
      startDate: startDate.toISOString(),
      limit: 100
    });

    // Calculate statistics
    const eventsByType: Record<string, number> = {};
    const userCounts: Record<string, { name: string; count: number }> = {};

    for (const log of logs) {
      const eventType = log.metadata?.event_type || log.action_type;
      eventsByType[eventType] = (eventsByType[eventType] || 0) + 1;
      
      const userKey = log.user_id;
      if (!userCounts[userKey]) {
        userCounts[userKey] = { 
          name: (log as AuditLogEntry & { user?: { first_name?: string } }).user?.first_name || 'Unknown User', 
          count: 0 
        };
      }
      userCounts[userKey].count++;
    }

    const topUsers = Object.entries(userCounts)
      .map(([userId, data]) => ({
        userId,
        userName: data.name,
        eventCount: data.count
      }))
      .sort((a, b) => b.eventCount - a.eventCount)
      .slice(0, 5);

    return {
      totalEvents: logs.length,
      eventsByType,
      topUsers,
      recentActivity: logs.slice(0, 10)
    };
  }
}